import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, take, filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';

/**
 * Guest Guard
 * Prevents authenticated users from accessing guest-only routes (login, signup)
 * Redirects to dashboard if user is already authenticated
 * Waits for Firebase auth initialization to prevent race conditions
 */
@Injectable({
  providedIn: 'root'
})
export class GuestGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Wait for Firebase auth to finish loading before checking user state
    return combineLatest([
      this.authService.loading$,
      this.authService.currentUser$
    ]).pipe(
      filter(([loading]) => !loading), // Wait until loading is complete
      take(1),
      map(([_, user]) => {
        if (!user) {
          // User is not authenticated, allow access to guest page
          return true;
        }

        // Authenticated but unverified: go straight to /verify/email rather
        // than bouncing off the dashboard, which AuthGuard would reject anyway.
        if (!user.emailVerified) {
          return this.router.createUrlTree(['/verify/email']);
        }

        return this.router.createUrlTree(['/dashboard']);
      })
    );
  }
}
